import { FormikProps } from 'formik';
import { Campaign } from '../../../../../types/Campaign.type';
import {
  AllPlatform,
  FormType,
  SocialInput,
  PersonaSocialInput,
  SocialPlatform,
} from '../../../../../types/FormTypes';

import { BasicButtonSelectionInput } from '../../../../shared/inputs/BasicButtonSelectionInput';
import {
  socialDefaultPlatformIcons,
  socialDefaultPlatformOptions,
  suggestedDemographics,
  suggestedTones,
} from './DefaultOptions';

type PlatformInputsProps<T> = {
  formikProps: FormikProps<T>;
  disabled: boolean;
};

export function SocialPlatformInputs<T extends SocialInput, PersonaSocialInput>({
  formikProps,
  disabled,
}: PlatformInputsProps<T>) {
  const newValueHandler = (newValue: string) => {
    formikProps.setFieldValue('platform', newValue);
    // If the suggested demographic was previously chosen, keep it selected in the new form type
    if (
      formikProps.values.targetDemographic.some((demographic) =>
        demographic.includes('suggested')
      )
    ) {
      let newDemographics = formikProps.values.targetDemographic || [];
      newDemographics = newDemographics.filter(
        (demographic) => !demographic.includes('suggested')
      );
      const newSuggestedDemographic =
        suggestedDemographics[FormType.SOCIAL][newValue as SocialPlatform];
      newDemographics.unshift(newSuggestedDemographic);
      formikProps.setFieldValue('targetDemographic', newDemographics);
    }

    // If the suggested tone was previously chosen, keep it selected in the new form type
    if (formikProps.values.tone.some((tone) => tone.includes('suggested'))) {
      let newTone = formikProps.values.tone || [];
      newTone = newTone.filter((tone) => !tone.includes('suggested'));
      const newSuggestedTone =
        suggestedTones[FormType.SOCIAL][newValue as SocialPlatform];
      newTone.unshift(newSuggestedTone);
      formikProps.setFieldValue('tone', newTone);
    }
  };

  return (
    <BasicButtonSelectionInput
      id="platform"
      label={
        <div>
          <h2>What social media platform are you posting to?</h2>
          <p>Please select a platform</p>
        </div>
      }
      defaultOptions={socialDefaultPlatformOptions}
      defaultOptionIcons={Object.values(socialDefaultPlatformIcons)}
      value={formikProps.values.platform}
      setValue={newValueHandler}
      disabled={disabled}
      errors={formikProps.errors.platform as string}
      touched={formikProps.touched.platform ? true : false}
      onBlur={formikProps.handleBlur}
    />
  );
}

export function AllPlatformInputs<T extends Campaign>({
  formikProps,
  disabled,
}: PlatformInputsProps<T>) {
  const newValueHandler = (newValue: string) => {
    formikProps.setFieldValue('platform', newValue);
    // If the suggested demographic was previously chosen, keep it selected in the new form type if needed
    if (
      formikProps.values.targetDemographic.some((demographic) =>
        demographic.includes('suggested')
      )
    ) {
      let newDemographics = formikProps.values.targetDemographic || [];
      newDemographics = newDemographics.filter(
        (demographic) => !demographic.includes('suggested')
      );
      if (Object.values(SocialPlatform).includes(newValue as SocialPlatform)) {
        const newSuggestedDemographic =
          suggestedDemographics[FormType.SOCIAL][newValue as SocialPlatform];
        newDemographics.unshift(newSuggestedDemographic);
      }
      formikProps.setFieldValue('targetDemographic', newDemographics);
    }

    // If the suggested tone was previously chosen, keep it selected in the new form type if needed
    if (formikProps.values.tone.some((tone) => tone.includes('suggested'))) {
      let newTone = formikProps.values.tone || [];
      newTone = newTone.filter((tone) => !tone.includes('suggested'));
      if (Object.values(SocialPlatform).includes(newValue as SocialPlatform)) {
        const newSuggestedTone =
          suggestedTones[FormType.SOCIAL][newValue as SocialPlatform];
        newTone.unshift(newSuggestedTone);
      }
      formikProps.setFieldValue('tone', newTone);
    }
  };

  return (
    <BasicButtonSelectionInput
      id="platform"
      label={
        <div>
          <h2>What kind of post are you marketing?</h2>
          <p>Please select a marketing medium.</p>
        </div>
      }
      defaultOptions={Object.values(AllPlatform)}
      defaultOptionIcons={Object.values(socialDefaultPlatformIcons)}
      value={formikProps.values.platform}
      setValue={newValueHandler}
      disabled={disabled}
      errors={formikProps.errors.platform as string}
      touched={formikProps.touched.platform ? true : false}
      onBlur={formikProps.handleBlur}
    />
  );
}
